import { createCompanyApi } from "@/api/services/main/company";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { SectionNavLayout, TNavSection } from "@/components/commons/SectionNavLayout";
import { CompanySectionCard } from "@/features/company/components/CompanySectionCard";
import { BasicInfoSection } from "@/features/company/components/sections/BasicInfoSection";
import { BillingRecipientSection } from "@/features/company/components/sections/BillingRecipientSection";
import { BillingTimingSection } from "@/features/company/components/sections/BillingTimingSection";
import { InternalStaffSection } from "@/features/company/components/sections/InternalStaffSection";
import { OtherInfoSection } from "@/features/company/components/sections/OtherInfoSection";
import {
  COMPANY_COLUMN_GAP,
  COMPANY_CONTENT_WIDTH,
  COMPANY_LAYOUT_WIDTH,
  COMPANY_NAV_WIDTH,
  COMPANY_PAGE_TITLE_CREATE,
  COMPANY_SECTION_ID,
  COMPANY_SECTION_TITLE,
  LEAVE_CONFIRM_MESSAGE,
} from "@/features/company/constants/sections";
import { TCompanyForm, companySchema, toCompanyFormValues } from "@/features/company/types/company-form";
import { convertFormDataToCreateCompanyReqDto } from "@/features/company/utils/company-form-converts";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { ButtonNew } from "@/modules/irohana-components/elements/ButtonNew";
import { Form } from "@/modules/irohana-components/elements/Form";
import { TopPage } from "@/modules/irohana-components/elements/TopPageNew";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, styled } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

/**
 * ACC20C02 取引先登録・編集.
 * All sections are stacked on a single page with the anchor nav — no tabs.
 * Saving is not wired yet — the 取引先 registration / update APIs (ACC20A02 / ACC20A03) do not exist.
 */
const CompanyForm: FC = () => {
  const navigate = useNavigate();
  // 保存に成功したら未保存確認のブロッカーを外してから遷移する。
  const [blockerEnabled, setBlockerEnabled] = useState(true);

  const methods = useForm<TCompanyForm>({
    resolver: yupResolver(companySchema) as Resolver<TCompanyForm>,
    defaultValues: toCompanyFormValues(),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const sections = useMemo<TNavSection[]>(
    () => [
      {
        id: COMPANY_SECTION_ID.basicInfo,
        title: COMPANY_SECTION_TITLE.basicInfo,
        content: (
          <CompanySectionCard title={COMPANY_SECTION_TITLE.basicInfo}>
            <BasicInfoSection />
          </CompanySectionCard>
        ),
      },
      {
        id: COMPANY_SECTION_ID.billingRecipient,
        title: COMPANY_SECTION_TITLE.billingRecipient,
        content: (
          <CompanySectionCard title={COMPANY_SECTION_TITLE.billingRecipient}>
            <BillingRecipientSection />
          </CompanySectionCard>
        ),
      },
      {
        id: COMPANY_SECTION_ID.internalStaff,
        title: COMPANY_SECTION_TITLE.internalStaff,
        content: (
          <CompanySectionCard title={COMPANY_SECTION_TITLE.internalStaff}>
            <InternalStaffSection />
          </CompanySectionCard>
        ),
      },
      {
        id: COMPANY_SECTION_ID.billingTiming,
        title: COMPANY_SECTION_TITLE.billingTiming,
        content: (
          <CompanySectionCard title={COMPANY_SECTION_TITLE.billingTiming}>
            <BillingTimingSection />
          </CompanySectionCard>
        ),
      },
      {
        id: COMPANY_SECTION_ID.otherInfo,
        title: COMPANY_SECTION_TITLE.otherInfo,
        content: (
          <CompanySectionCard title={COMPANY_SECTION_TITLE.otherInfo}>
            <OtherInfoSection />
          </CompanySectionCard>
        ),
      },
    ],
    [],
  );

  // ACC20A02 取引先登録。更新(ACC20A03)は編集画面を作るときに分岐させる。
  const handleSubmit = async (data: TCompanyForm) => {
    try {
      const rs = await createCompanyApi(convertFormDataToCreateCompanyReqDto(data));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      methods.reset(data);
      setBlockerEnabled(false);
      navigate(AppRoutes.home);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={handleSubmit} schema={companySchema} enableBlocker={blockerEnabled} customMessageLeave={LEAVE_CONFIRM_MESSAGE}>
      <Container>
        <TopPage title={COMPANY_PAGE_TITLE_CREATE} enablePageBack={false} />

        <SectionNavLayout
          sections={sections}
          navWidth={COMPANY_NAV_WIDTH}
          contentWidth={COMPANY_CONTENT_WIDTH}
          columnGap={COMPANY_COLUMN_GAP}
          footer={
            <FooterContainer>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <ButtonNew variant="tertiary" onClick={() => navigate(-1)}>
                  戻る
                </ButtonNew>
                <ButtonNew type="submit" variant="primary" startIcon={<IconSave />}>
                  保存
                </ButtonNew>
              </Stack>
            </FooterContainer>
          }
        />
      </Container>
    </Form>
  );
};

const Container = styled(Stack)`
  width: 100%;
  max-width: ${COMPANY_LAYOUT_WIDTH}px;
  margin: 0 auto;
  gap: 24px;
`;

export default CompanyForm;
